<script>
    import { environment } from "@app/store/envStore.svelte.js";
    import { showModal } from "@app/store/modalStore.svelte.js";
    import { eventGroupState, resetEventGroupState } from "@app/store/eventGroupStore.svelte.js";
    import { projectState, resetProjectState } from "@app/store/projectStore.svelte.js";
    import { authState, resetAuth } from "@app/store/authStore.svelte.js";
    import { can, canUseOfflineDesktop } from "@app/lib/entitlements.js";
    import { metadataState } from "@app/store/metadataStore.svelte.js";
    import { speakerState } from "@app/store/speakerStore.svelte.js";
    import { issueState } from "@app/store/issueStore.svelte.js";
    import { resetMarkerState } from "@app/store/markerStore.svelte.js";
    import { resetWatermarkState } from "@app/store/watermarkStore.svelte.js";
    import { resetHistory } from "@app/store/historyStore.svelte.js";
    import { tick } from "svelte";
    import { auth } from "@app/configs/firebase.js";
    import { signOut } from "firebase/auth";
    import { replace } from "svelte-spa-router";
    /* Firebase */
    import db from "@app/configs/firestore.js";
    import { doc as fsDoc, getDoc } from "firebase/firestore";
    /* CC LIB */
    import { saveAs } from "file-saver";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import * as m from '@app/paraglide/messages.js';
    import Menu from "../menu/Menu.svelte";
    import MenuItem from "../menu/MenuItem.svelte";
    import MenuDivider from "../menu/MenuDivider.svelte";
    import {
        KEYBOARD_SHORTCUTS_STORAGE_KEY,
        dispatchKeyboardShortcutsUpdated,
        getStoredKeyboardShortcuts,
    } from "@app/lib/keyboardShortcuts.js";

    let updateAvailable = $state(false);

    if (environment.online) {
        getDoc(fsDoc(db, "system", "creator"))
            .then((doc) => {
                if (doc.exists()) {
                    let data = doc.data();
                    updateAvailable = data.version !== environment.version;
                }
            });
    } else if (!environment.iframe) {
        /* Desktop licence (offline) edition: check the website when there is a connection; silent otherwise. */
        fetch("https://www.closedcaptioncreator.com/latest.json", { cache: "no-store" })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (data?.version) updateAvailable = data.version !== environment.version.replace(/-offline$/, "");
            })
            .catch(() => {});
    }

    function backupUserSettings() {
        console.log("Backing up User Settings!");
        // Define the settings keys we want to export
        const settingsKeys = [
            "cc-editor-defaults",
            "cc-ad-defaults-new",
            "cc-event-group-defaults",
            "cc-shortcuts",
            "cc-theme",
            "cc-project-defaults",
            "cc-style-preset",
            "cc-timing-and-sync-settings",
            "cc-ai-import-settings",
        ];

        try {
            // Create an object to store all settings
            const settings = {};

            // Iterate through each key and get its value from localStorage
            settingsKeys.forEach((key) => {
                const value = localStorage.getItem(key);
                if (value) {
                    try {
                        // Try to parse the value as JSON
                        settings[key] = JSON.parse(value);
                    } catch (e) {
                        // If parsing fails, store as string
                        settings[key] = value;
                    }
                }
            });

            // Create metadata for the export
            const exportData = {
                version: "1.0",
                timestamp: new Date().toISOString(),
                settings: settings,
            };

            // Convert settings object to a JSON string with pretty printing
            const jsonString = JSON.stringify(exportData, null, 2);

            // Create a Blob with the JSON data
            const blob = new Blob([jsonString], { type: "application/json" });

            // Generate filename with timestamp
            const filename = `user-settings-${new Date().toISOString().split("T")[0]}.json`;

            // Save the file
            saveAs(blob, filename);
        } catch (error) {
            console.error("Error exporting settings:", error);
            throw new Error("Failed to export settings");
        }
    }

    const validateImportedSettings = (data) => {
        // Check if the data has the expected structure
        if (!data || typeof data !== "object") return false;
        if (!data.version || !data.timestamp || !data.settings) return false;

        return true;
    };

    async function importUserSettings() {
        try {
            // Show file input dialog
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.import_settings(),
                html: `
        <div class="mb-3">
          <label for="settings-file-input" class="form-label">
            ${m.choose_settings_file()}
          </label>
          <input type="file"
                 class="form-control"
                 accept=".json" 
                 id="settings-file-input">
        </div>
      `,
                showCancelButton: true,
                confirmButtonText: m.import_btn(),
                cancelButtonText: m.cancel(),
                didOpen: () => {
                    // Initialize Bootstrap file input
                    const input = document.getElementById(
                        "settings-file-input",
                    );
                    input.addEventListener("change", () => {
                        // Bootstrap's form-control class handles the file name display automatically
                    });
                },
                preConfirm: () => {
                    const fileInput = document.getElementById(
                        "settings-file-input",
                    );
                    if (!fileInput.files.length) {
                        Swal.showValidationMessage(m.please_select_file());
                        return false;
                    }
                    return fileInput.files[0];
                },
            });

            if (!result.isConfirmed || !result.value) {
                return;
            }

            // Read the file
            const file = result.value;
            const fileContent = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = (e) => reject(e);
                reader.readAsText(file);
            });

            // Parse and validate the JSON
            const settingsData = JSON.parse(fileContent);

            if (!validateImportedSettings(settingsData)) {
                throw new Error("Invalid settings file format");
            }

            // Show confirmation dialog
            const confirmResult = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.confirm_import(),
                text: m.overwrite_settings_confirm(),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: m.yes_import_settings(),
                cancelButtonText: m.cancel(),
            });

            if (!confirmResult.isConfirmed) {
                return;
            }

            // Import the settings
            Object.entries(settingsData.settings).forEach(([key, value]) => {
                console.log(JSON.stringify(value));
                //Check if value is already a string
                if (typeof value === "string") {
                    localStorage.setItem(key, value);
                } else {
                    localStorage.setItem(key, JSON.stringify(value));
                }                
            });

            if (Object.prototype.hasOwnProperty.call(settingsData.settings, KEYBOARD_SHORTCUTS_STORAGE_KEY)) {
                dispatchKeyboardShortcutsUpdated(
                    getStoredKeyboardShortcuts(environment.mac),
                );
            }

            // Show success message
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.success(),
                text: m.settings_imported_success(),
                icon: "success",
            });
        } catch (error) {
            console.error("Error importing settings:", error);

            // Show error message
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.error(),
                text: error.message || m.failed_import_settings(),
                icon: "error",
            });
        }
    }

    async function resetApplication() {
        /* Reset Stores */
        if (eventGroupState[projectState.selected]) {
            eventGroupState[projectState.selected].selected = [];
            await tick();
        }

        projectState.selected = false;
        await tick();
        resetEventGroupState();
        await tick();
        resetProjectState();
        metadataState.length = 0;
        speakerState.length = 0;
        issueState.length = 0;
        resetMarkerState();
        resetWatermarkState();
        resetAuth();
        resetHistory();
        localStorage.clear();

        if (environment.online) {
            signOut(auth);
        }

        if (environment.electron) {
            const ipcRenderer = window.ipcRenderer;
            ipcRenderer.invoke("quit");
        } else {
            replace("/login");
        }
    }
</script>

<Menu id="help" label={m.help()} ariaLabel={m.help_menu()}>
    {#if can("teams") && environment.online && authState?.user?.role === "admin"}
        <MenuItem
            label={m.team_management()}
            icon="bi-people-fill"
            id="teamManagement"
            onSelect={() => showModal("teamManager")} />
        <MenuDivider />
    {/if}
    {#if environment.online}
        {#if authState?.user?.role === "admin"}
            <MenuItem
                label={m.manage_subscription()}
                icon="bi-credit-card"
                onSelect={() => showModal("subscriptionManagement")} />
            <MenuItem
                label={m.reporting_usage()}
                icon="bi-graph-up"
                onSelect={() => showModal("usage")} />
        {/if}
        <MenuItem
            label={m.online_user_guide()}
            icon="bi-book"
            href="https://www.closedcaptioncreator.com/docs"
            target="_blank" />
        <MenuItem
            label={m.contact_support()}
            icon="bi-life-preserver"
            href="https://www.closedcaptioncreator.com/support.html"
            target="_blank" />
        {#if canUseOfflineDesktop()}
            <MenuItem
                label={m.offline_activation()}
                icon="bi-key"
                onSelect={() => showModal("offlineActivation")} />
        {/if}
        <MenuItem
            label={m.restore_event_groups()}
            icon="bi-arrow-counterclockwise"
            onSelect={() => showModal("autoSave")} />
        <MenuDivider />
    {:else if !environment.iframe}
        <MenuItem
            label={m.license_title()}
            icon="bi-key"
            onSelect={() => showModal("licenseInfo")} />
        <MenuDivider />
    {/if}
    <MenuItem
        label={m.backup_user_settings()}
        icon="bi-download"
        id="backupSettings"
        onSelect={backupUserSettings} />
    <MenuItem
        label={m.import_user_settings()}
        icon="bi-upload"
        onSelect={importUserSettings} />
    <MenuItem
        label={m.reset_application()}
        icon="bi-exclamation-triangle"
        onSelect={resetApplication} />
    <MenuDivider />
    <MenuItem
        label={m.whats_new()}
        icon="bi-lightbulb"
        onSelect={() => showModal("tips")} />
    <MenuItem
        label={m.about_cc_creator()}
        icon="bi-info-circle"
        onSelect={() => showModal("about")} />
    {#if updateAvailable}
        <MenuItem
            class="text-warning"
            label={m.update_available()}
            icon="bi-stars"
            href="https://www.closedcaptioncreator.com/downloads.html"
            target="_blank" />
    {:else if environment.online}
        <MenuItem label={m.no_updates_available()} disabled />
    {/if}
</Menu>
